import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { BrandFilterSelect } from '../CuratedWatches/BrandFilterSelect';
import { ReferenceEditor } from '../CuratedWatches/ReferenceEditor';
import {
  getWatchBrands,
  getWatchBrandById,
  getImageUrl,
  getWatchReference,
  uploadSandboxRecognitionImage,
  sandboxRecognizeImage,
  sandboxSearchByDescription,
  logAiSandboxTest,
  getRecentAiSandboxTests,
  type SandboxImageResult,
  type SandboxImageMatch,
  type SandboxTextResult,
  type SandboxTextMatch,
  type SandboxWebMatch,
  type SandboxWebMatchSource,
  type SandboxExtractedAttributes,
  type SandboxScoreBreakdown,
  type AiSandboxLogRecord,
} from '../../services/firebase';
import { ensureUploadableImage } from '../../utils/imageConversion';
import { formatMultiLookupValues } from '../../utils/multiLookupValues';
import { webMatchProvenanceBadge } from '../../utils/webMatchProvenance';
import type { WatchBrand, WatchReference } from '../../types';

type Mode = 'image' | 'text';

// The backend caps the free-text hint; mirror it so the composed hint the user
// sees is what actually reaches the model.
const HINT_MAX_LEN = 200;
const DESCRIPTION_MIN = 8;
const DESCRIPTION_MAX = 600;

function copyJson(value: unknown) {
  const text = JSON.stringify(value, null, 2);
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).then(
      () => toast.success('Copied to clipboard'),
      () => toast.error('Copy failed'),
    );
  } else {
    toast.error('Clipboard unavailable');
  }
}

export function AISandbox() {
  const [mode, setMode] = useState<Mode>('image');

  // Self-hosted ref editor. Clicking a matched ref opens the full ReferenceEditor
  // in a modal OVER the sandbox (mirroring CrossBrandCalibreSearch and
  // ManufacturerBrandDrillDown) so all sandbox state — uploaded image, hint,
  // brand filter, results, recent tests — is preserved: the sandbox never
  // unmounts. Both the match title and the "Show full spec" link call openRef.
  const [editingRef, setEditingRef] = useState<{
    reference: WatchReference;
    brand: WatchBrand;
  } | null>(null);
  // Bridge to the editor's unsaved-changes guard so the Modal's Esc/backdrop
  // close routes through the same confirm dialog as the editor's Back button.
  const editorExitGuardRef = useRef<((proceed: () => void) => void) | null>(
    null,
  );

  // Shared brand list.
  const [brands, setBrands] = useState<WatchBrand[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(true);

  // Recent tests.
  const [recent, setRecent] = useState<AiSandboxLogRecord[]>([]);

  // Image panel.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [hint, setHint] = useState('');
  const [imageBrandId, setImageBrandId] = useState('');
  const [imageLoading, setImageLoading] = useState(false);
  const [imageResult, setImageResult] = useState<SandboxImageResult | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  // Retry-a-previous-test: when the original upload still exists in Storage we
  // reuse its path instead of forcing a re-upload. `retryImagePath` is the stored
  // path to send to the callable; `retryImageUrl` is its download URL for preview.
  // Both cleared the moment the user picks a fresh file.
  const [retryImagePath, setRetryImagePath] = useState<string | null>(null);
  const [retryImageUrl, setRetryImageUrl] = useState<string | null>(null);

  // Text panel.
  const [description, setDescription] = useState('');
  const [textBrandId, setTextBrandId] = useState('');
  const [textLoading, setTextLoading] = useState(false);
  const [textResult, setTextResult] = useState<SandboxTextResult | null>(null);
  const [textError, setTextError] = useState<string | null>(null);

  const refreshRecent = useCallback(() => {
    getRecentAiSandboxTests(20)
      .then(setRecent)
      .catch((err) => console.error('Failed to load recent sandbox tests:', err));
  }, []);

  useEffect(() => {
    getWatchBrands()
      .then((list) => {
        const sorted = [...list].sort((a, b) =>
          (a.name ?? '').localeCompare(b.name ?? ''),
        );
        setBrands(sorted);
      })
      .catch((err) => {
        console.error('Failed to load brands:', err);
        toast.error('Failed to load brands');
      })
      .finally(() => setBrandsLoading(false));
    refreshRecent();
  }, [refreshRecent]);

  // Local preview for the selected image.
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const brandNameById = useCallback(
    (id: string) => brands.find((b) => b.id === id)?.name ?? '',
    [brands],
  );

  // Open a matched reference in the modal editor. Fetches the freshest ref + its
  // brand doc (a ref lives in a brand subcollection, so both ids are required);
  // guards both against a missing doc before opening.
  const openRef = useCallback(
    async (brandId: string, referenceId: string) => {
      try {
        const [reference, brand] = await Promise.all([
          getWatchReference(brandId, referenceId),
          getWatchBrandById(brandId),
        ]);
        if (!reference || !brand) {
          toast.error('Reference not found');
          return;
        }
        setEditingRef({ reference, brand });
      } catch (err) {
        console.error('Failed to open reference:', err);
        toast.error('Failed to open reference');
      }
    },
    [],
  );

  // The image callable has NO brand argument — brand is parsed out of the hint
  // text. So a chosen brand is prepended into the hint, and we show the exact
  // composed string that will be sent.
  const composedHint = useMemo(() => {
    const brandName = brandNameById(imageBrandId).trim();
    return [brandName, hint.trim()].filter(Boolean).join(' — ').slice(0, HINT_MAX_LEN);
  }, [brandNameById, imageBrandId, hint]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    setFile(selected);
    // A fresh pick supersedes any reused retry image.
    setRetryImagePath(null);
    setRetryImageUrl(null);
    setImageResult(null);
    setImageError(null);
  };

  const handleIdentify = async () => {
    if (!file && !retryImagePath) return;
    setImageLoading(true);
    setImageError(null);
    setImageResult(null);
    const start = performance.now();
    try {
      // Reuse the retry image's stored path when the user hasn't picked a fresh
      // file; otherwise upload the selected file.
      const imagePath = file
        ? await uploadSandboxRecognitionImage(await ensureUploadableImage(file))
        : (retryImagePath as string);
      const result = await sandboxRecognizeImage(imagePath, composedHint);
      const durationMs = Math.round(performance.now() - start);
      setImageResult(result);
      toast.success(
        result.noMatch ? 'Done — no catalogue match' : 'Identify complete',
      );
      await logAiSandboxTest({
        mode: 'image',
        input: { hint: composedHint, brandId: imageBrandId, imagePath },
        response: result,
        usedBonus: result.usedBonus,
        usedIntro: result.usedIntro,
        durationMs,
      }).catch((err) => console.error('Log write failed:', err));
      refreshRecent();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setImageError(msg);
      toast.error(`Identify failed: ${msg}`);
    } finally {
      setImageLoading(false);
    }
  };

  const descriptionLen = description.trim().length;
  const descriptionValid =
    descriptionLen >= DESCRIPTION_MIN && descriptionLen <= DESCRIPTION_MAX;
  const canSearch = !!textBrandId && descriptionValid && !textLoading;

  const handleSearch = async () => {
    if (!textBrandId) {
      toast.error('Text search requires a brand.');
      return;
    }
    if (!descriptionValid) {
      toast.error(
        `Description must be ${DESCRIPTION_MIN}–${DESCRIPTION_MAX} characters.`,
      );
      return;
    }
    setTextLoading(true);
    setTextError(null);
    setTextResult(null);
    const start = performance.now();
    // The endpoint resolves a brand id OR exact name; send the name so it works
    // whether or not the id matches the endpoint's brand directory.
    const brandArg = brandNameById(textBrandId) || textBrandId;
    try {
      const result = await sandboxSearchByDescription(
        brandArg,
        description.trim(),
      );
      const durationMs = Math.round(performance.now() - start);
      setTextResult(result);
      toast.success(
        result.noMatch ? 'Done — no catalogue match' : `Search: ${result.status}`,
      );
      await logAiSandboxTest({
        mode: 'text',
        input: { description: description.trim(), brandId: textBrandId, brand: brandArg },
        response: result,
        usedBonus: result.usedBonus,
        usedIntro: result.usedIntro,
        durationMs,
      }).catch((err) => console.error('Log write failed:', err));
      refreshRecent();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setTextError(msg);
      toast.error(`Search failed: ${msg}`);
    } finally {
      setTextLoading(false);
    }
  };

  // Repopulate the input panel from a logged test so the admin can tweak and
  // re-run. Never auto-submits — the user still hits Identify / Search.
  const handleRetry = async (record: AiSandboxLogRecord) => {
    const input = record.input ?? {};
    if (record.mode === 'text') {
      setMode('text');
      setTextResult(null);
      setTextError(null);
      setDescription(typeof input.description === 'string' ? input.description : '');
      setTextBrandId(typeof input.brandId === 'string' ? input.brandId : '');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Image mode.
    setMode('image');
    setImageResult(null);
    setImageError(null);
    const storedBrandId = typeof input.brandId === 'string' ? input.brandId : '';
    const composed = typeof input.hint === 'string' ? input.hint : '';
    // The logged hint is the *composed* string (brand folded in). Strip the
    // brand prefix so re-composing on submit reproduces the same hint rather
    // than double-prepending the brand.
    const brandName = storedBrandId ? brandNameById(storedBrandId).trim() : '';
    const prefix = brandName ? `${brandName} — ` : '';
    const rawHint =
      prefix && composed.startsWith(prefix) ? composed.slice(prefix.length) : composed;
    setImageBrandId(storedBrandId);
    setHint(rawHint);
    // Clear any prior file/retry selection; we re-establish the retry image below.
    setFile(null);
    setRetryImagePath(null);
    setRetryImageUrl(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const imagePath = typeof input.imagePath === 'string' ? input.imagePath : '';
    if (!imagePath) {
      toast('Inputs restored — no image path in the log; select an image.', {
        icon: 'ℹ️',
      });
      return;
    }
    // Reuse the original upload only if it still exists in Storage.
    try {
      const url = await getImageUrl(imagePath);
      setRetryImagePath(imagePath);
      setRetryImageUrl(url);
      toast.success('Inputs and image restored — tweak, then Identify.');
    } catch {
      toast('Inputs restored — original image is no longer available; select a new one.', {
        icon: 'ℹ️',
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">AI Sandbox</h2>
        <p className="text-sm text-gray-500 mt-1">
          Test the live <code>recognizeWatchFromImage</code> and{' '}
          <code>searchReferencesByDescription</code> backends directly. Runs in{' '}
          <strong>sandbox mode</strong> — super-admin only, no AI credit is
          spent. Iterate prompts in the AI Prompts tab, then test here.
        </p>
      </div>

      {/* Mode toggle */}
      <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
        {(['image', 'text'] as Mode[]).map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              mode === m
                ? 'bg-brand-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {m === 'image' ? 'Image Identify' : 'Text Search'}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Input column */}
        <div className="lg:col-span-1 space-y-4">
          {mode === 'image' ? (
            <div className="bg-white shadow rounded-lg p-4 space-y-3">
              <h3 className="font-medium text-gray-900">Image + hint</h3>

              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-brand-400 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {previewUrl || retryImageUrl ? (
                  <img
                    src={previewUrl ?? retryImageUrl ?? undefined}
                    alt="Selected"
                    className="max-h-48 mx-auto rounded"
                  />
                ) : (
                  <p className="text-sm text-gray-400">Click to select an image</p>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.heic,.heif"
                onChange={handleFileSelect}
                className="hidden"
              />
              {file ? (
                <p className="text-xs text-gray-500 truncate">
                  {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </p>
              ) : retryImagePath ? (
                <p className="text-xs text-gray-500 truncate">
                  Reusing the original upload — click to replace.
                </p>
              ) : null}

              <BrandFilterSelect
                brands={brands}
                loading={brandsLoading}
                value={imageBrandId}
                onChange={setImageBrandId}
                label="Brand (optional — folded into the hint)"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hint (free text)
                </label>
                <textarea
                  value={hint}
                  onChange={(e) => setHint(e.target.value)}
                  rows={3}
                  placeholder="e.g. blue dial, brown leather strap"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
              </div>

              <div className="text-xs text-gray-500">
                <span className="font-medium text-gray-700">Hint sent:</span>{' '}
                {composedHint ? (
                  <code className="break-words">{composedHint}</code>
                ) : (
                  <em>(none)</em>
                )}
                <span
                  className={`ml-1 ${
                    composedHint.length >= HINT_MAX_LEN ? 'text-amber-600' : ''
                  }`}
                >
                  · {composedHint.length}/{HINT_MAX_LEN}
                </span>
              </div>

              <Button
                className="w-full"
                onClick={handleIdentify}
                disabled={(!file && !retryImagePath) || imageLoading}
                loading={imageLoading}
              >
                Identify
              </Button>

              {imageError && (
                <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 break-words">
                  {imageError}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white shadow rounded-lg p-4 space-y-3">
              <h3 className="font-medium text-gray-900">Description + brand</h3>

              <BrandFilterSelect
                brands={brands}
                loading={brandsLoading}
                value={textBrandId}
                onChange={setTextBrandId}
                label="Brand (required)"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  placeholder="e.g. steel diver with black dial and rotating bezel"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                />
                <p
                  className={`text-xs mt-1 ${
                    descriptionLen > 0 && !descriptionValid
                      ? 'text-amber-600'
                      : 'text-gray-500'
                  }`}
                >
                  {descriptionLen}/{DESCRIPTION_MAX} · min {DESCRIPTION_MIN}
                </p>
              </div>

              <Button
                className="w-full"
                onClick={handleSearch}
                disabled={!canSearch}
                loading={textLoading}
              >
                Search
              </Button>

              {textError && (
                <div className="p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 break-words">
                  {textError}
                </div>
              )}
              <p className="text-xs text-gray-400">
                Requires the enrichment feature flag to be on (else a 403 is
                surfaced).
              </p>
            </div>
          )}
        </div>

        {/* Results column */}
        <div className="lg:col-span-2 space-y-4">
          {mode === 'image' ? (
            <ImageResults
              result={imageResult}
              loading={imageLoading}
              onOpenReference={openRef}
            />
          ) : (
            <TextResults
              result={textResult}
              loading={textLoading}
              onOpenReference={openRef}
            />
          )}
        </div>
      </div>

      <RecentTests recent={recent} onRefresh={refreshRecent} onRetry={handleRetry} />

      {/* Ref editor OVER the sandbox — closing returns to the exact sandbox state
          (the sandbox never unmounts). Close on save/delete, matching
          ManufacturerBrandDrillDown. */}
      {editingRef && (
        <Modal
          isOpen
          onClose={() => {
            const guard = editorExitGuardRef.current;
            if (guard) guard(() => setEditingRef(null));
            else setEditingRef(null);
          }}
          title={`${editingRef.brand.name ?? 'Brand'} · ${
            editingRef.reference.reference || 'reference'
          }`}
          size="full"
        >
          <ReferenceEditor
            key={editingRef.reference.id}
            reference={editingRef.reference}
            brand={editingRef.brand}
            exitGuardRef={editorExitGuardRef}
            onBack={() => setEditingRef(null)}
            onSaved={() => setEditingRef(null)}
            onDeleted={() => setEditingRef(null)}
          />
        </Modal>
      )}
    </div>
  );
}

// -- Results: image ---------------------------------------------------------

function ImageResults({
  result,
  loading,
  onOpenReference,
}: {
  result: SandboxImageResult | null;
  loading: boolean;
  onOpenReference?: (brandId: string, referenceId: string) => void;
}) {
  if (loading && !result) return <EmptyState label="Identifying…" />;
  if (!result) return <EmptyState label="Select an image and click Identify." />;

  const webCount = Array.isArray(result.webMatches) ? result.webMatches.length : 0;

  return (
    <div className="space-y-4">
      <IdentifyHeader result={result} />

      <FlagsRow
        flags={[
          { label: 'noMatch', on: result.noMatch },
          { label: 'usedBonus', on: result.usedBonus },
          { label: 'usedIntro', on: result.usedIntro },
          { label: `webMatches: ${webCount}`, on: webCount > 0, neutral: true },
          { label: `matches: ${result.matches?.length ?? 0}`, on: true, neutral: true },
        ]}
      />

      {(result.matches ?? []).map((m, i) => (
        <ImageMatchCard
          key={m.referenceId ?? i}
          match={m}
          onOpenReference={onOpenReference}
        />
      ))}

      <WebMatchesSection webMatches={result.webMatches} />

      <JsonBlock value={result} />
    </div>
  );
}

/**
 * The run's input shown ONCE, above the match cards: the uploaded photo beside
 * the attributes the vision model extracted from it. The extraction is identical
 * across every candidate, so hoisting it here keeps the cards free for the
 * per-candidate extracted-vs-ref comparison.
 *
 * `extractedAttributes` rides on each match rather than the response root, so we
 * take it off the first match that carries one. A `noMatch` run has no matches
 * and therefore nothing to show — the image still renders on its own.
 */
function IdentifyHeader({ result }: { result: SandboxImageResult }) {
  const attrs = (result.matches ?? []).find((m) => m.extractedAttributes)
    ?.extractedAttributes;

  return (
    <div className="bg-white shadow rounded-lg p-4 flex flex-col md:flex-row gap-4">
      <UploadedImagePreview imagePath={result.imagePath} />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-gray-700 mb-1">
          Extracted attributes
        </p>
        {attrs ? (
          <AttributesGrid attrs={attrs} />
        ) : (
          <p className="text-xs text-gray-400">No attributes extracted.</p>
        )}
      </div>
    </div>
  );
}

/**
 * Thumbnail of the photo the admin uploaded for this run, resolved from the
 * returned Storage `imagePath` via a download URL. Renders nothing if the path
 * is missing or the object can't be resolved (e.g. cleaned up).
 */
function UploadedImagePreview({ imagePath }: { imagePath?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!imagePath) {
      setUrl(null);
      setFailed(false);
      return;
    }
    let active = true;
    setUrl(null);
    setFailed(false);
    getImageUrl(imagePath)
      .then((u) => {
        if (active) setUrl(u);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [imagePath]);

  if (!imagePath || failed) return null;

  return (
    <div className="shrink-0 md:w-1/3">
      <p className="text-xs font-medium text-gray-700 mb-2">Your uploaded image</p>
      {url ? (
        <img
          src={url}
          alt="Uploaded"
          className="max-h-64 w-auto rounded border border-gray-100"
        />
      ) : (
        <div className="h-24 flex items-center justify-center text-xs text-gray-400">
          Loading image…
        </div>
      )}
    </div>
  );
}

function ImageMatchCard({
  match,
  onOpenReference,
}: {
  match: SandboxImageMatch;
  onOpenReference?: (brandId: string, referenceId: string) => void;
}) {
  const title =
    [match.brandName, match.model].filter(Boolean).join(' ') ||
    match.productName ||
    match.referenceId;
  return (
    <div className="bg-white shadow rounded-lg p-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <RefTitle
          title={title}
          brandId={match.brandId}
          referenceId={match.referenceId}
          onOpenReference={onOpenReference}
        />
        <div className="flex items-center gap-2">
          <MatchPill confidence={match.confidence} rawLabel={match.matchLabel} />
          <ConfidenceBadge value={match.confidence} />
        </div>
      </div>
      <div className="text-xs text-gray-500 space-y-0.5 mb-2">
        {match.reference && <div>Ref: {match.reference}</div>}
        <div className="font-mono truncate">{match.referenceId}</div>
        {match.webPromoted && (
          <span className="inline-block text-brand-700">web-promoted</span>
        )}
      </div>
      <SlotComparison breakdown={match.scoreBreakdown} />
      <MatchSpecDetails
        brandId={match.brandId}
        referenceId={match.referenceId}
        brandLabel={match.brandName}
        fallbackImageUrl={match.imageUrl}
        onOpenReference={onOpenReference}
      />
    </div>
  );
}

/** One line of the extraction grid. `flag` marks a value as self-contradictory. */
interface AttrRow {
  label: string;
  value?: string;
  flag?: string;
}

/** Multi-valued extraction fields, joined for display. */
function joinSlugs(values?: string[]): string | undefined {
  return values && values.length ? values.join(', ') : undefined;
}

/**
 * The vision model's extraction for the run. EVERY slot is listed — the ones the
 * model didn't fill read as "—", because "extracted nothing here" is itself the
 * signal being tested, and hiding it would hide the difference between the model
 * missing a field and the reference lacking it.
 *
 * The dial-detail fields added by watchlock PR #878 sit in labelled sections
 * below the main grid rather than in it: there are eleven of them, they only
 * apply to a subset of watches, and the Lume block has a gating rule the flat
 * grid has no way to express.
 */
function AttributesGrid({ attrs }: { attrs: SandboxExtractedAttributes }) {
  const rows: Array<[string, string | undefined]> = [
    ['Brand', attrs.brand],
    ['Model', attrs.model],
    ['Reference', attrs.reference],
    ['Collection', attrs.collection],
    ['Dial color', attrs.dialColor],
    ['Dial finish', attrs.dialFinish],
    ['Numerals', attrs.dialNumerals],
    ['Hands color', attrs.handsColor],
    // 0 subdials / no crown guards are real answers, so stringify BEFORE the
    // truthiness filter below — `String(0)` and 'No' both survive it.
    ['Subdials', attrs.subdials != null ? String(attrs.subdials) : undefined],
    ['Date window', attrs.dateWindow],
    ['Case material', attrs.caseMaterial],
    ['Case finish', attrs.caseFinish],
    ['Case shape', attrs.caseShape],
    ['Bezel type', attrs.bezelType],
    ['Bezel color', attrs.bezelColor],
    [
      'Crown guards',
      attrs.crownGuards != null ? (attrs.crownGuards ? 'Yes' : 'No') : undefined,
    ],
    ['Movement', attrs.movementType],
    ['Hand style', attrs.handStyle],
    ['Strap material', attrs.strapMaterial],
    ['Strap type', attrs.strapType],
    [
      'Approx size',
      attrs.approxCaseSizeMm != null ? `${attrs.approxCaseSizeMm} mm` : undefined,
    ],
    [
      'Complications',
      attrs.complications && attrs.complications.length
        ? attrs.complications.join(', ')
        : undefined,
    ],
    [
      'Visible text',
      attrs.visibleText && attrs.visibleText.length
        ? attrs.visibleText.join(' · ')
        : undefined,
    ],
    [
      'Overall conf.',
      attrs.overallConfidence != null
        ? `${(attrs.overallConfidence * 100).toFixed(0)}%`
        : undefined,
    ],
  ];
  return (
    <>
      <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs">
        {rows.map(([k, v]) => (
          <div key={k} className="flex gap-1">
            <dt className="text-gray-500 shrink-0">{k}:</dt>
            <dd className={v ? 'text-gray-900 break-words' : 'text-gray-300'}>
              {v || '—'}
            </dd>
          </div>
        ))}
      </dl>
      <DialDetailSections attrs={attrs} />
    </>
  );
}

/**
 * The eleven dial-detail fields watchlock PR #878 added to the identify
 * extraction, grouped the way the reference editor groups the catalogue fields
 * behind them so the two read the same way side by side. Labels match the ref
 * editor's terminology (US "color", per the lookup collections) but follow this
 * grid's sentence case.
 */
function DialDetailSections({ attrs }: { attrs: SandboxExtractedAttributes }) {
  const dateRows: AttrRow[] = [
    { label: 'Date wheel color', value: attrs.dateWheelColor },
    { label: 'Date wheel text color', value: attrs.dateWheelTextColor },
  ];

  // Presence gates the rest: a `none` answer means there is no lume to
  // describe, so the other three are suppressed — UNLESS the model filled one
  // anyway, in which case they are shown and flagged. That contradiction is the
  // exact kind of extraction bug the sandbox exists to surface, so hiding it
  // would defeat the purpose of the gate.
  const lumeDetail: AttrRow[] = [
    { label: 'Lume material', value: attrs.lumeMaterial },
    { label: 'Lume day color', value: attrs.lumeDayColor },
    { label: 'Lume glow color', value: attrs.lumeGlowColor },
  ];
  const presenceIsNone = attrs.lumePresence === 'none';
  const contradicts = presenceIsNone && lumeDetail.some((r) => r.value);
  const lumeRows: AttrRow[] = [
    { label: 'Lume presence', value: attrs.lumePresence },
    ...(presenceIsNone && !contradicts
      ? []
      : lumeDetail.map((r) =>
          contradicts && r.value
            ? { ...r, flag: 'Set despite lume presence = none' }
            : r,
        )),
  ];

  const subdialRows: AttrRow[] = [
    { label: 'Subdial types', value: joinSlugs(attrs.subdialTypes) },
    { label: 'Chapter ring', value: attrs.chapterRing },
    { label: 'Minute track', value: attrs.minuteTrack },
  ];

  const otherRows: AttrRow[] = [
    { label: 'Dial scales', value: joinSlugs(attrs.dialScales) },
    { label: 'Dial features', value: joinSlugs(attrs.dialFeatures) },
    { label: 'Logo type', value: attrs.logoType },
  ];

  return (
    <>
      <AttrSection title="Date" rows={dateRows} />
      <AttrSection title="Lume" rows={lumeRows} />
      <AttrSection title="Subdials & chapter ring" rows={subdialRows} />
      <AttrSection title="Dial detail" rows={otherRows} />
    </>
  );
}

/** One labelled block of the extraction grid. */
function AttrSection({ title, rows }: { title: string; rows: AttrRow[] }) {
  return (
    <div className="mt-2 border-t border-gray-100 pt-2">
      <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wide mb-1">
        {title}
      </p>
      <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 text-xs">
        {rows.map((r) => (
          <div key={r.label} className="flex gap-1">
            <dt className="text-gray-500 shrink-0">{r.label}:</dt>
            <dd
              className={
                r.flag
                  ? 'text-amber-700 break-words'
                  : r.value
                    ? 'text-gray-900 break-words'
                    : 'text-gray-300'
              }
              title={r.flag}
            >
              {r.value || '—'}
              {r.flag && <span className="ml-1 text-amber-600">⚠</span>}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// -- Results: extracted-vs-ref comparison -----------------------------------

/**
 * Human labels for the scorer's slot keys, which mirror the
 * `SandboxExtractedAttributes` keys. Anything not listed still renders, via the
 * de-camelCase fallback in `slotLabel` — a slot the backend adds later shows up
 * with a readable-enough name instead of vanishing.
 */
const SLOT_LABELS: Record<string, string> = {
  brand: 'Brand',
  model: 'Model',
  reference: 'Reference',
  collection: 'Collection',
  dialColor: 'Dial color',
  dialFinish: 'Dial finish',
  dialNumerals: 'Numerals',
  handsColor: 'Hands color',
  handStyle: 'Hand style',
  subdials: 'Subdials',
  dateWindow: 'Date window',
  caseMaterial: 'Case material',
  caseFinish: 'Case finish',
  caseShape: 'Case shape',
  caseSizeMm: 'Case size',
  bezelType: 'Bezel type',
  bezelColor: 'Bezel color',
  crownGuards: 'Crown guards',
  movementType: 'Movement',
  strapMaterial: 'Strap material',
  strapType: 'Strap type',
  // Dial detail — watchlock PR #878.
  subdialTypes: 'Subdial types',
  chapterRing: 'Chapter ring',
  minuteTrack: 'Minute track',
  dialScales: 'Dial scales',
  dialFeatures: 'Dial features',
  logoType: 'Logo type',
  lumePresence: 'Lume presence',
  lumeMaterial: 'Lume material',
  lumeDayColor: 'Lume day color',
  lumeGlowColor: 'Lume glow color',
  dateWheelColor: 'Date wheel color',
  dateWheelTextColor: 'Date wheel text color',
};

/**
 * Slots whose value is a SET on at least one side. The scorer already compares
 * these set-wise server-side — by the time a slot reaches us both sides are
 * flat comma-joined strings — but it joins each side in its own source order,
 * so a set-equal pair can print as two differently-ordered lists and read as a
 * disagreement next to a green ● . Sorting the tokens for display makes the
 * scorer's verdict legible; it changes nothing about the verdict itself.
 */
const MULTI_VALUE_SLOTS = new Set([
  'subdialTypes',
  'dialScales',
  'dialFeatures',
  'dateWheelTextColor',
]);

function slotLabel(key: string): string {
  if (SLOT_LABELS[key]) return SLOT_LABELS[key];
  const spaced = key.replace(/([A-Z])/g, ' $1').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Presentation for one side of a comparison row. Returns null for "nothing
 * here" so the caller can render a muted dash: `false` and `0` are real answers
 * and must survive, which a truthiness check would eat.
 *
 * `multi` sorts the comma-separated tokens so the two sides of a set-equal slot
 * line up visually — see `MULTI_VALUE_SLOTS`.
 */
function formatSlotValue(value: unknown, multi = false): string | null {
  if (value == null) return null;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    const parts = value.filter((v) => v != null).map((v) => String(v));
    return (multi ? [...parts].sort() : parts).join(', ') || null;
  }
  const text = String(value).trim();
  if (!text) return null;
  if (!multi) return text;
  return text
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
    .sort()
    .join(', ');
}

/**
 * Sort rank and styling per `penaltyReason`. Mismatches sort first so the reason
 * a candidate scored badly is the first thing visible; unrecognised reasons fall
 * through to a neutral row rather than being dropped.
 */
const SLOT_STATUS: Record<
  string,
  { rank: number; mark: string; tone: string; accent: string }
> = {
  mismatch: {
    rank: 0,
    mark: '✕',
    tone: 'text-red-600',
    accent: 'border-red-300',
  },
  near: {
    rank: 1,
    mark: '≈',
    tone: 'text-amber-600',
    accent: 'border-amber-300',
  },
  match: {
    rank: 2,
    mark: '●',
    tone: 'text-green-600',
    accent: 'border-green-300',
  },
  'not-extracted': {
    rank: 3,
    mark: '○',
    tone: 'text-gray-400',
    accent: 'border-gray-200',
  },
  'gated-off': {
    rank: 4,
    mark: '·',
    tone: 'text-gray-300',
    accent: 'border-gray-100',
  },
};

const NEUTRAL_STATUS = {
  rank: 2.5,
  mark: '·',
  tone: 'text-gray-400',
  accent: 'border-gray-200',
};

/**
 * The "On ref" cell when there is nothing to print.
 *
 * A blank cell used to be one glyph for three unrelated situations, and the
 * ambiguity was expensive: a screen full of dashes reads as "the scorer is
 * ignoring these fields" when it may equally mean "these references have not
 * been enriched" or "this attribute is not in the index yet". Chasing the first
 * reading is what turned a projection gap into a hunt for a scorer bug.
 *
 * Three states, deliberately:
 *   - `false` — the backend positively says this reference holds no value. An
 *     empty-set glyph, because it is a statement, not a gap.
 *   - `undefined` — an older backend that never sent the field. The original
 *     dash, because we genuinely do not know.
 *   - `true` with no formatted value — the reference HAS something the
 *     formatter could not render. Worth seeing: it means a projection is
 *     landing values in a shape the sandbox does not understand.
 */
function EmptyRefCell({ present }: { present?: boolean }) {
  if (present === false) {
    return (
      <span className="text-gray-400" title="This reference holds no value for this slot">
        ∅ none on ref
      </span>
    );
  }
  if (present === true) {
    return (
      <span
        className="text-amber-600"
        title="The reference carries a value the sandbox could not format — likely a shape the projection changed"
      >
        ⚠ unreadable
      </span>
    );
  }
  return <span title="This backend did not report whether the reference has a value">—</span>;
}

/**
 * Per-candidate answer to "what did we see vs what does this ref actually say".
 * Fed by the sandbox-only `scoreBreakdown`; when the backend didn't send one
 * (older responses) we say so rather than falling back to re-printing the
 * extraction, which is already shown once in the header.
 */
function SlotComparison({ breakdown }: { breakdown?: SandboxScoreBreakdown }) {
  const rows = useMemo(() => {
    const slots = breakdown?.slots;
    if (!slots || typeof slots !== 'object') return [];
    return Object.entries(slots)
      .map(([key, slot]) => ({
        key,
        label: slotLabel(key),
        extracted: formatSlotValue(slot?.extracted, MULTI_VALUE_SLOTS.has(key)),
        candidate: formatSlotValue(slot?.candidate, MULTI_VALUE_SLOTS.has(key)),
        // Tri-state, not a boolean. `undefined` means the backend predates the
        // field and we must not claim to know either way — the column then
        // renders exactly as it did before. `false` is a positive statement that
        // the reference holds nothing here, which is a different thing from a
        // value we simply failed to format.
        candidatePresent:
          typeof slot?.candidatePresent === 'boolean' ? slot.candidatePresent : undefined,
        weight: typeof slot?.weight === 'number' ? slot.weight : 0,
        status: SLOT_STATUS[slot?.penaltyReason] ?? NEUTRAL_STATUS,
      }))
      .sort((a, b) => a.status.rank - b.status.rank || b.weight - a.weight);
  }, [breakdown]);

  if (!rows.length) {
    return (
      <div className="mt-2 border-t border-gray-100 pt-2">
        <p className="text-xs text-gray-400">Score breakdown not available.</p>
      </div>
    );
  }

  return (
    <div className="mt-2 border-t border-gray-100 pt-2">
      <p className="text-xs font-medium text-gray-700 mb-1">Extracted vs ref</p>
      <div className="grid grid-cols-3 gap-x-3 gap-y-0.5 text-xs">
        {/* pl matches the label cells' status-dot indent below. */}
        <div className="text-gray-400 font-medium pl-3.5">Slot</div>
        <div className="text-gray-400 font-medium">Extracted</div>
        <div className="text-gray-400 font-medium">On ref</div>
        {rows.map((r) => (
          <Fragment key={r.key}>
            <div
              className={`flex gap-1 border-l-2 pl-1.5 ${r.status.accent} min-w-0`}
            >
              <span className={`shrink-0 ${r.status.tone}`}>{r.status.mark}</span>
              <span className="text-gray-500 break-words">{r.label}</span>
            </div>
            <div
              className={
                r.extracted ? 'text-gray-900 break-words' : 'text-gray-300'
              }
            >
              {r.extracted ?? '—'}
            </div>
            <div
              className={
                r.candidate ? 'text-gray-900 break-words' : 'text-gray-300'
              }
            >
              {r.candidate ?? <EmptyRefCell present={r.candidatePresent} />}
            </div>
          </Fragment>
        ))}
      </div>
    </div>
  );
}

// -- Results: text ----------------------------------------------------------

function TextResults({
  result,
  loading,
  onOpenReference,
}: {
  result: SandboxTextResult | null;
  loading: boolean;
  onOpenReference?: (brandId: string, referenceId: string) => void;
}) {
  if (loading && !result) return <EmptyState label="Searching…" />;
  if (!result) return <EmptyState label="Enter a description and click Search." />;

  const webCount = Array.isArray(result.webMatches) ? result.webMatches.length : 0;

  return (
    <div className="space-y-4">
      <FlagsRow
        flags={[
          { label: `status: ${result.status ?? '—'}`, on: true, neutral: true },
          ...(result.statusNote
            ? [{ label: result.statusNote, on: true, neutral: true }]
            : []),
          { label: 'noMatch', on: !!result.noMatch },
          { label: 'usedBonus', on: !!result.usedBonus },
          { label: 'usedIntro', on: !!result.usedIntro },
          { label: `webMatches: ${webCount}`, on: webCount > 0, neutral: true },
          {
            label: `matches: ${result.matches?.length ?? 0}`,
            on: true,
            neutral: true,
          },
        ]}
      />

      {(result.matches ?? []).map((m, i) => (
        <TextMatchCard
          key={m.referenceId ?? i}
          match={m}
          onOpenReference={onOpenReference}
        />
      ))}

      <WebMatchesSection webMatches={result.webMatches} />

      <JsonBlock value={result} />
    </div>
  );
}

function TextMatchCard({
  match,
  onOpenReference,
}: {
  match: SandboxTextMatch;
  onOpenReference?: (brandId: string, referenceId: string) => void;
}) {
  const title = [match.brand, match.model].filter(Boolean).join(' ') || match.referenceId;
  return (
    <div className="bg-white shadow rounded-lg p-4">
      <div className="flex items-center justify-between gap-2 mb-1">
        <RefTitle
          title={title}
          brandId={match.brandId}
          referenceId={match.referenceId}
          onOpenReference={onOpenReference}
        />
        <div className="flex items-center gap-2">
          <MatchPill confidence={match.confidence} rawLabel={match.matchLabel} />
          <ConfidenceBadge value={match.confidence} />
        </div>
      </div>
      <div className="text-xs text-gray-500 space-y-0.5">
        {match.refNumber && <div>Ref: {match.refNumber}</div>}
        <div className="font-mono truncate">{match.referenceId}</div>
        {match.webPromoted && (
          <span className="inline-block text-brand-700">web-promoted</span>
        )}
      </div>
      {match.reasons && match.reasons.length > 0 && (
        <ul className="mt-2 list-disc list-inside text-xs text-gray-700 space-y-0.5">
          {match.reasons.map((r, i) => (
            <li key={i}>{r}</li>
          ))}
        </ul>
      )}
      <MatchSpecDetails
        brandId={match.brandId}
        referenceId={match.referenceId}
        brandLabel={match.brand}
        fallbackImageUrl={match.imageUrl}
        onOpenReference={onOpenReference}
      />
    </div>
  );
}

// -- Results: web-discovered ------------------------------------------------

/** How many sources a web-match card shows before the "+N more" toggle. */
const WEB_SOURCES_VISIBLE = 3;

/**
 * Guard for hrefs built from AI web-search output: only http(s) URLs become
 * clickable links, so a `javascript:` (or any other scheme) coming back from
 * the model renders as inert text instead of an executable link.
 */
function safeHttpUrl(url?: string): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    return parsed.toString();
  } catch {
    return null;
  }
}

/** Short label for a source link: its title, else its host, else the raw URL. */
function webSourceLabel(source: SandboxWebMatchSource, safeUrl: string | null): string {
  const title = source.title?.trim();
  if (title) return title;
  if (safeUrl) {
    try {
      return new URL(safeUrl).hostname;
    } catch {
      return safeUrl;
    }
  }
  return source.url?.trim() || '(no url)';
}

/**
 * References the AI found by searching the web rather than our catalogue.
 * Deliberately styled apart from the catalogue match cards (amber accent + a
 * `web` badge) so the two are never confused, and with no ref-editor deep-link:
 * a web match has no referenceId because it isn't in the catalogue at all.
 * Renders nothing when the backend returned no web matches.
 */
function WebMatchesSection({ webMatches }: { webMatches?: SandboxWebMatch[] }) {
  const items = Array.isArray(webMatches) ? webMatches : [];
  if (items.length === 0) return null;

  return (
    <div className="bg-white shadow rounded-lg p-4">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="font-medium text-gray-900 text-sm">Web-Discovered Matches</h3>
        <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
          {items.length}
        </span>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        ⓘ Found by the AI's web-search fallback — these are <em>not</em> in the
        catalogue, so they can't be opened in the reference editor.
      </p>
      <div className="space-y-3">
        {items.map((m, i) => (
          <WebMatchCard key={`${m.referenceNormalized ?? m.reference ?? 'web'}-${i}`} match={m} />
        ))}
      </div>
    </div>
  );
}

/**
 * Which web pass found a row — `text`, `image`, or `both`.
 *
 * The colour and wording decision lives in `webMatchProvenanceBadge`, a pure
 * function, because this repo tests logic rather than rendering. Renders nothing
 * when the field is absent (every response predating it) or unrecognised —
 * `discoveredVia` is unvalidated backend JSON.
 */
function WebMatchProvenanceBadge({ via }: { via?: SandboxWebMatch['discoveredVia'] }) {
  const badge = webMatchProvenanceBadge(via);
  if (!badge) return null;

  return (
    <span
      className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
      title={badge.title}
    >
      {badge.label}
    </span>
  );
}

function WebMatchCard({ match }: { match: SandboxWebMatch }) {
  const [showAllSources, setShowAllSources] = useState(false);

  const sources = Array.isArray(match.sources) ? match.sources : [];
  const visible = showAllSources ? sources : sources.slice(0, WEB_SOURCES_VISIBLE);
  const hiddenCount = sources.length - visible.length;

  const reference = match.reference?.trim() || match.referenceNormalized?.trim();
  const meta = [match.brand?.trim(), match.brandId?.trim()].filter(Boolean).join(' · ');

  const thumbnail = safeHttpUrl(match.thumbnailUrl);

  return (
    <div className="border-l-2 border-amber-300 bg-amber-50/40 rounded-r pl-3 pr-2 py-2">
      {/* The picture the reverse image search found. Only image-pass findings
          carry one — the gpt-4o text search returns prose and citations. Its
          presence USED to be the only read on which pass produced a row; the
          provenance badge says so directly now, and says it for the corroborated
          rows a thumbnail could never distinguish from image-only ones. */}
      {thumbnail && (
        <img
          src={thumbnail}
          alt=""
          loading="lazy"
          className="float-right ml-2 mb-1 h-14 w-14 rounded object-cover border border-amber-200 bg-white"
        />
      )}
      <div className="flex items-start justify-between gap-2">
        <span className="font-mono font-semibold text-sm text-gray-900 break-all">
          {reference || '(no reference)'}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          <WebMatchProvenanceBadge via={match.discoveredVia} />
          <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">
            web
          </span>
          <ConfidenceBadge value={match.confidence} />
        </div>
      </div>

      {meta && <div className="text-xs text-gray-500 mt-0.5">{meta}</div>}

      {match.modelDescription && (
        <p className="text-xs text-gray-700 mt-1 break-words">
          {match.modelDescription}
        </p>
      )}

      {visible.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {visible.map((s, i) => {
            const href = safeHttpUrl(s.url);
            const label = webSourceLabel(s, href);
            return (
              <li key={i} className="text-xs truncate">
                {href ? (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 hover:text-brand-700 hover:underline"
                    title={href}
                  >
                    ↗ {label}
                  </a>
                ) : (
                  <span className="text-gray-400" title="Not a usable http(s) link">
                    {label}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setShowAllSources(true)}
          className="mt-1 text-xs text-brand-600 hover:text-brand-700"
        >
          + {hiddenCount} more source{hiddenCount === 1 ? '' : 's'}
        </button>
      )}
    </div>
  );
}

// -- Recent tests -----------------------------------------------------------

function RecentTests({
  recent,
  onRefresh,
  onRetry,
}: {
  recent: AiSandboxLogRecord[];
  onRefresh: () => void;
  onRetry: (record: AiSandboxLogRecord) => void;
}) {
  return (
    <div className="bg-white shadow rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-gray-900">
          Recent tests
          <span className="ml-2 text-xs font-normal text-gray-400">
            ({recent.length})
          </span>
        </h3>
        <button
          onClick={onRefresh}
          className="text-sm text-brand-600 hover:text-brand-700"
        >
          Refresh
        </button>
      </div>
      {recent.length === 0 ? (
        <p className="text-sm text-gray-400">No tests logged yet.</p>
      ) : (
        <div className="space-y-2">
          {recent.map((r) => (
            <details
              key={r.id}
              className="border border-gray-200 rounded-lg px-3 py-2"
            >
              <summary className="cursor-pointer text-sm flex items-center gap-2 flex-wrap">
                <span
                  className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    r.mode === 'image'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-purple-100 text-purple-700'
                  }`}
                >
                  {r.mode}
                </span>
                <span className="text-gray-500 text-xs">
                  {r.at ? format(r.at, 'MMM d, HH:mm:ss') : '—'}
                </span>
                <span className="text-gray-700 truncate max-w-[40ch]">
                  {summarizeInput(r)}
                </span>
                <span className="text-gray-400 text-xs">· {r.durationMs}ms</span>
                <button
                  onClick={(e) => {
                    // Inside a <summary> — stop the click from toggling the
                    // <details> open/closed while retrying.
                    e.preventDefault();
                    e.stopPropagation();
                    onRetry(r);
                  }}
                  className="ml-auto px-2 py-0.5 rounded border border-brand-200 text-xs font-medium text-brand-700 hover:bg-brand-50"
                  title={
                    r.mode === 'image'
                      ? 'Repopulate the inputs; reuses the original image if it still exists.'
                      : 'Repopulate the description and brand.'
                  }
                >
                  Retry
                </button>
              </summary>
              <div className="mt-2">
                <button
                  onClick={() => copyJson(r.response)}
                  className="text-xs text-brand-600 hover:text-brand-700 mb-1"
                >
                  Copy response
                </button>
                <JsonBlock value={r.response} maxHeight="max-h-[30vh]" />
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

function summarizeInput(r: AiSandboxLogRecord): string {
  const input = r.input ?? {};
  if (r.mode === 'image') {
    const h = typeof input.hint === 'string' ? input.hint : '';
    return h || '(no hint)';
  }
  const d = typeof input.description === 'string' ? input.description : '';
  return d || '(no description)';
}

// -- Shared bits ------------------------------------------------------------

function EmptyState({ label }: { label: string }) {
  return (
    <div className="bg-white shadow rounded-lg p-8 text-center text-gray-400 text-sm">
      {label}
    </div>
  );
}

function ConfidenceBadge({ value }: { value?: number }) {
  if (value == null || Number.isNaN(value)) {
    return <span className="text-xs text-gray-400">—</span>;
  }
  let color = 'bg-red-100 text-red-700';
  if (value >= 0.8) color = 'bg-green-100 text-green-700';
  else if (value >= 0.5) color = 'bg-yellow-100 text-yellow-700';
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {(value * 100).toFixed(1)}%
    </span>
  );
}

/**
 * Match title that deep-links into the Curated Watches ref editor when a
 * navigation handler + both ids are available (a ref lives in a brand
 * subcollection, so brandId is required). Falls back to plain text otherwise —
 * e.g. an image match the backend couldn't resolve to a catalogue brand.
 */
function RefTitle({
  title,
  brandId,
  referenceId,
  onOpenReference,
}: {
  title: string;
  brandId?: string;
  referenceId?: string;
  onOpenReference?: (brandId: string, referenceId: string) => void;
}) {
  if (onOpenReference && brandId && referenceId) {
    return (
      <button
        type="button"
        onClick={() => onOpenReference(brandId, referenceId)}
        className="font-medium text-brand-600 hover:text-brand-700 hover:underline text-left"
        title="Open in the reference editor"
      >
        {title}
      </button>
    );
  }
  return <span className="font-medium text-gray-900">{title}</span>;
}

/**
 * Confidence-derived match pill, mirroring watchlock's `AIMatchResultsList` so
 * the admin sandbox and the RN app show the same label for the same confidence.
 * Thresholds: ≥0.8 Strong match, ≥0.5 Possible match, else Possible alternative.
 */
function matchPillFromConfidence(confidence?: number): {
  label: string;
  className: string;
} {
  const c =
    typeof confidence === 'number' && !Number.isNaN(confidence) ? confidence : null;
  if (c == null) return { label: '', className: '' };
  if (c >= 0.8) return { label: 'Strong match', className: 'bg-green-600 text-white' };
  if (c >= 0.5) return { label: 'Possible match', className: 'bg-amber-500 text-white' };
  return { label: 'Possible alternative', className: 'bg-gray-400 text-white' };
}

function MatchPill({
  confidence,
  rawLabel,
}: {
  confidence?: number;
  rawLabel?: string;
}) {
  const { label, className } = matchPillFromConfidence(confidence);
  if (!label) {
    // No usable confidence — fall back to the raw backend label if present.
    return rawLabel ? (
      <span className="text-xs text-gray-500">{rawLabel}</span>
    ) : null;
  }
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${className}`}>
      {label}
    </span>
  );
}

/**
 * Collapsible "Show full spec" section on a match card: lazily fetches the
 * matched reference doc on first expand and renders a compact read-only spec so
 * the admin can see what the match actually is without leaving the sandbox.
 * A ref lives in a brand subcollection, so it can only be fetched with a
 * brandId — renders nothing when brandId is absent (image match the backend
 * couldn't resolve to a catalogue brand).
 */
function MatchSpecDetails({
  brandId,
  referenceId,
  brandLabel,
  fallbackImageUrl,
  onOpenReference,
}: {
  brandId?: string;
  referenceId?: string;
  brandLabel?: string;
  fallbackImageUrl?: string;
  onOpenReference?: (brandId: string, referenceId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [reference, setReference] = useState<WatchReference | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetched, setFetched] = useState(false);

  const canFetch = !!brandId && !!referenceId;

  useEffect(() => {
    if (!open || fetched || !canFetch) return;
    let active = true;
    setLoading(true);
    setError(null);
    getWatchReference(brandId as string, referenceId as string)
      .then((r) => {
        if (!active) return;
        if (r) setReference(r);
        else setError('Reference not found.');
        setFetched(true);
      })
      .catch((e) => {
        if (!active) return;
        setError(e instanceof Error ? e.message : String(e));
        setFetched(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, fetched, canFetch, brandId, referenceId]);

  // No brandId → can't fetch the ref (and can't link it either); render nothing.
  if (!canFetch) return null;

  return (
    <div className="mt-2 border-t border-gray-100 pt-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-xs font-medium text-brand-600 hover:text-brand-700 flex items-center gap-1"
        aria-expanded={open}
      >
        <span
          className={`inline-block transition-transform ${open ? 'rotate-90' : ''}`}
        >
          ▶
        </span>
        {open ? 'Hide full spec' : 'Show full spec'}
      </button>

      {open && (
        <div className="mt-2">
          {onOpenReference && brandId && referenceId && (
            <button
              type="button"
              onClick={() => onOpenReference(brandId, referenceId)}
              className="text-xs font-medium text-brand-600 hover:text-brand-700 hover:underline mb-2"
            >
              Open in ref editor →
            </button>
          )}
          {loading && <p className="text-xs text-gray-400">Loading spec…</p>}
          {error && <p className="text-xs text-red-600">{error}</p>}
          {reference && (
            <SpecBody
              reference={reference}
              brandLabel={brandLabel}
              fallbackImageUrl={fallbackImageUrl}
            />
          )}
        </div>
      )}
    </div>
  );
}

/** Resolves a reference thumbnail: direct URL → callable fallback → Storage path. */
function RefThumb({
  directUrl,
  storagePath,
  fallbackUrl,
}: {
  directUrl?: string;
  storagePath?: string;
  fallbackUrl?: string;
}) {
  const preferred = directUrl ?? fallbackUrl ?? null;
  const [url, setUrl] = useState<string | null>(preferred);

  useEffect(() => {
    if (preferred) {
      setUrl(preferred);
      return;
    }
    if (!storagePath) {
      setUrl(null);
      return;
    }
    let active = true;
    getImageUrl(storagePath)
      .then((u) => {
        if (active) setUrl(u);
      })
      .catch(() => {
        if (active) setUrl(null);
      });
    return () => {
      active = false;
    };
  }, [preferred, storagePath]);

  if (!url) return null;
  return (
    <img
      src={url}
      alt="Reference"
      className="max-h-40 rounded border border-gray-100 mb-2"
    />
  );
}

/** Compact, read-only spec grid for a matched reference. */
function SpecBody({
  reference: r,
  brandLabel,
  fallbackImageUrl,
}: {
  reference: WatchReference;
  brandLabel?: string;
  fallbackImageUrl?: string;
}) {
  const cc = r.calibreCache;
  const cas = r.case;
  const dial = r.dialAndHands;
  const strap = r.strap;

  const mm = (n?: number) => (n != null ? `${n} mm` : undefined);

  const waterResistance =
    cas?.waterResistance ??
    (cas?.waterResistanceM != null ? `${cas.waterResistanceM}m` : undefined);
  const bezel = [r.bezel, r.bezelType].filter(Boolean).join(' · ') || undefined;
  const calibreName = cc?.canonicalName || r.calibre;
  const powerReserve = cc?.powerReserveHours ?? r.movement?.powerReserveHours;
  const frequency = cc?.frequencyVph ?? r.movement?.frequencyVph;
  const jewels = cc?.jewels ?? r.movement?.jewels;
  const complications =
    r.features && r.features.length
      ? r.features.join(', ')
      : r.functions && r.functions.length
        ? r.functions.join(', ')
        : undefined;

  const sections: Array<{
    title: string;
    rows: Array<[string, string | undefined]>;
  }> = [
    {
      title: 'Identity',
      rows: [
        ['Brand', brandLabel],
        ['Model', r.model],
        ['Reference', r.reference],
        ['Collection', r.collection],
        ['Series', r.series],
        ['Type', r.watchType],
      ],
    },
    {
      title: 'Case',
      rows: [
        ['Material', cas?.material],
        ['Size', mm(cas?.sizeMm ?? cas?.diameterMm ?? cas?.diameter)],
        ['Thickness', mm(cas?.heightMm ?? cas?.thickness)],
        ['Shape', cas?.shape],
        ['Water resistance', waterResistance],
        // `crystal` is multi-select (string[]) at the top level while
        // `case.crystal` is still a scalar. Flatten it, and `|| undefined` so an
        // empty result falls through to `glass` instead of shadowing it.
        [
          'Crystal',
          cas?.crystal ??
            (formatMultiLookupValues(r.crystal) || undefined) ??
            r.glass,
        ],
        ['Bezel', bezel],
      ],
    },
    {
      title: 'Dial & hands',
      rows: [
        ['Dial color', dial?.color ?? r.dial ?? r.color],
        ['Dial style', r.dialStyle],
        ['Finish', dial?.finish],
        // Both are multi-select (string[]) since v1.51.0, and both still read
        // back as a bare string pre-migration — `formatMultiLookupValues`
        // absorbs either. `||` rather than `??` so an empty `indexes` falls
        // through to `numerals` instead of shadowing it, matching Crystal above.
        [
          'Indexes',
          formatMultiLookupValues(dial?.indexes) ||
            formatMultiLookupValues(dial?.numerals) ||
            undefined,
        ],
        ['Hands', dial?.hands],
      ],
    },
    {
      title: 'Strap',
      rows: [
        ['Material', strap?.material],
        ['Color', strap?.color],
        ['Width', mm(strap?.width)],
        ['Buckle', strap?.buckleType],
      ],
    },
    {
      title: 'Movement',
      rows: [
        ['Calibre', calibreName],
        ['Type', cc?.movementType ?? r.movementType ?? r.movement?.type],
        ['Power reserve', powerReserve != null ? `${powerReserve} h` : undefined],
        ['Frequency', frequency != null ? `${frequency} vph` : undefined],
        ['Jewels', jewels != null ? String(jewels) : undefined],
      ],
    },
    {
      title: 'Complications',
      rows: [['Features', complications]],
    },
  ];

  const presentSections = sections
    .map((s) => ({ ...s, rows: s.rows.filter(([, v]) => v) }))
    .filter((s) => s.rows.length > 0);

  return (
    <div>
      <RefThumb
        directUrl={r.imageUrls?.[0]}
        storagePath={r.images?.[0]?.storagePath}
        fallbackUrl={fallbackImageUrl}
      />
      {presentSections.length === 0 ? (
        <p className="text-xs text-gray-400">No spec fields populated.</p>
      ) : (
        <div className="space-y-2">
          {presentSections.map((s) => (
            <div key={s.title}>
              <p className="text-xs font-medium text-gray-700 mb-0.5">{s.title}</p>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                {s.rows.map(([k, v]) => (
                  <div key={k} className="flex gap-1">
                    <dt className="text-gray-500 shrink-0">{k}:</dt>
                    <dd className="text-gray-900 break-words">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FlagsRow({
  flags,
}: {
  flags: Array<{ label: string; on: boolean; neutral?: boolean }>;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {flags.map((f, i) => (
        <span
          key={i}
          className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
            f.neutral
              ? 'bg-gray-50 text-gray-600 border-gray-200'
              : f.on
                ? 'bg-brand-50 text-brand-700 border-brand-200'
                : 'bg-gray-50 text-gray-400 border-gray-200'
          }`}
        >
          {f.label}
        </span>
      ))}
    </div>
  );
}

function JsonBlock({
  value,
  maxHeight = 'max-h-[50vh]',
}: {
  value: unknown;
  maxHeight?: string;
}) {
  return (
    <div className="bg-white shadow rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-medium text-gray-900 text-sm">Raw JSON</h3>
        <button
          onClick={() => copyJson(value)}
          className="text-sm text-brand-600 hover:text-brand-700"
        >
          Copy
        </button>
      </div>
      <pre
        className={`bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto ${maxHeight} text-xs leading-relaxed`}
      >
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}
